/**
 * StateSouvenirsRegistry.ts
 * Authoritative catalog of all 15 bespoke 2.5D Cozy Storybook Gouache State & Regional Souvenirs
 * (`512x512` transparent RGBA PNGs on round warm oak pedestals with brass plaques and #3B2314 ink outlines),
 * covering all 50 US States + Washington D.C. with full legacy alias normalization.
 */

export interface StateSouvenirBuffSpec {
  type: 'HAPPINESS_PASSIVE' | 'ENERGY_PASSIVE' | 'WORK_PAY_PASSIVE' | 'STUDY_SPEED_PASSIVE';
  value: number;
  description: string;
  passiveEnergyPerMin: number;
  passiveHappinessPerMin: number;
}

export interface StateSouvenirEntry {
  id: string;
  primaryStateCode: string;
  primaryStateName: string;
  coveredStateCodes: readonly string[];
  isFlagship: boolean;
  name: string;
  vietnameseName: string;
  description: string;
  pngUrl: string;
  /** Tight non-transparent [sx, sy, sw, sh] bounding box inside the 512x512 PNG */
  bbox: readonly [number, number, number, number];
  /** [anchorX, anchorY] floor-contact anchor fraction inside bbox for HomeScene isometric placement */
  floorAnchor: readonly [number, number];
  /** Logical canvas [width, height] for MicroCanvasEngine / Phaser room texture synthesis */
  logicalCanvasSize: readonly [number, number];
  /** Recommended display [width, height] in PhaserAssetPackRegistry */
  recommendedDisplaySize: readonly [number, number];
  buff: StateSouvenirBuffSpec;
  aliases: readonly string[];
}

export const STATE_SOUVENIR_ENTRIES: readonly StateSouvenirEntry[] = [
  // 1-5: Five Flagship US States (names strictly match FLAGSHIP_STATES)
  {
    id: 'souvenir_ny_statue_liberty',
    primaryStateCode: 'NY',
    primaryStateName: 'New York',
    coveredStateCodes: ['NY'],
    isFlagship: true,
    name: 'Statue of Liberty Mini Figurine',
    vietnameseName: 'Tượng Nữ Thần Tự Do Mini',
    description: 'Mô hình Tượng Nữ Thần Tự Do tinh xảo trên bệ gỗ sồi đính bảng đồng thủ công.',
    pngUrl: './assets/souvenirs/souvenir_ny_statue_liberty.png',
    bbox: [154, 35, 204, 442],
    floorAnchor: [0.5, 0.9],
    logicalCanvasSize: [22, 34],
    recommendedDisplaySize: [56, 68],
    buff: {
      type: 'HAPPINESS_PASSIVE',
      value: 2.0,
      description: '+2% Tinh thần thụ động mỗi phút',
      passiveEnergyPerMin: 0,
      passiveHappinessPerMin: 0.25,
    },
    aliases: ['souvenir_ny_statue', 'souvenir_ny_atlas'],
  },
  {
    id: 'souvenir_ca_golden_gate',
    primaryStateCode: 'CA',
    primaryStateName: 'California',
    coveredStateCodes: ['CA'],
    isFlagship: true,
    name: 'Golden Gate Bridge Miniature',
    vietnameseName: 'Sa Bàn Cầu Cổng Vàng',
    description: 'Mô hình Cầu Cổng Vàng màu đỏ cam vươn qua làn sương mù vịnh San Francisco trên bệ gỗ sồi.',
    pngUrl: './assets/souvenirs/souvenir_ca_golden_gate.png',
    bbox: [44, 35, 423, 442],
    floorAnchor: [0.5, 0.87],
    logicalCanvasSize: [30, 24],
    recommendedDisplaySize: [64, 60],
    buff: {
      type: 'ENERGY_PASSIVE',
      value: 3.0,
      description: '+3% Hồi phục Năng lượng khi nghỉ ngơi',
      passiveEnergyPerMin: 0.15,
      passiveHappinessPerMin: 0,
    },
    aliases: ['souvenir_ca_bridge', 'souvenir_ca_atlas'],
  },
  {
    id: 'souvenir_tx_longhorn',
    primaryStateCode: 'TX',
    primaryStateName: 'Texas',
    coveredStateCodes: ['TX'],
    isFlagship: true,
    name: 'Vintage Longhorn & Stetson Hat',
    vietnameseName: 'Bò Longhorn & Mũ Cao Bồi Stetson',
    description: 'Mô hình bò sừng dài Longhorn đội mũ cao bồi Stetson trên bệ gỗ sồi đính sao vàng Lone Star.',
    pngUrl: './assets/souvenirs/souvenir_tx_longhorn.png',
    bbox: [48, 35, 415, 442],
    floorAnchor: [0.5, 0.84],
    logicalCanvasSize: [28, 22],
    recommendedDisplaySize: [64, 58],
    buff: {
      type: 'WORK_PAY_PASSIVE',
      value: 2.0,
      description: '+2% Tiền thưởng thù lao trên mọi ca làm việc',
      passiveEnergyPerMin: 0,
      passiveHappinessPerMin: 0.15,
    },
    aliases: ['souvenir_tx_hat', 'souvenir_tx_atlas'],
  },
  {
    id: 'souvenir_fl_rocket_flamingo',
    primaryStateCode: 'FL',
    primaryStateName: 'Florida',
    coveredStateCodes: ['FL'],
    isFlagship: true,
    name: 'Space Shuttle & Flamingo Diorama',
    vietnameseName: 'Tên Lửa Vũ Trụ & Hồng Hạc Nhiệt Đới',
    description: 'Sa bàn kết hợp tàu vũ trụ Cape Canaveral và chim hồng hạc biển nhiệt đới trên bệ gỗ sồi.',
    pngUrl: './assets/souvenirs/souvenir_fl_rocket_flamingo.png',
    bbox: [73, 35, 366, 442],
    floorAnchor: [0.5, 0.89],
    logicalCanvasSize: [22, 36],
    recommendedDisplaySize: [58, 68],
    buff: {
      type: 'HAPPINESS_PASSIVE',
      value: 3.0,
      description: '+3% Tinh thần thụ động mỗi phút',
      passiveEnergyPerMin: 0,
      passiveHappinessPerMin: 0.25,
    },
    aliases: ['souvenir_fl_rocket', 'souvenir_fl_atlas'],
  },
  {
    id: 'souvenir_wa_space_needle',
    primaryStateCode: 'WA',
    primaryStateName: 'Washington',
    coveredStateCodes: ['WA'],
    isFlagship: true,
    name: 'Space Needle Desktop Model',
    vietnameseName: 'Tháp Space Needle & Núi Tuyết Rainier',
    description: 'Mô hình tháp Space Needle bên rặng thông Evergreen tạo cảm hứng tập trung học tập.',
    pngUrl: './assets/souvenirs/souvenir_wa_space_needle.png',
    bbox: [88, 35, 336, 442],
    floorAnchor: [0.5, 0.88],
    logicalCanvasSize: [20, 38],
    recommendedDisplaySize: [56, 72],
    buff: {
      type: 'STUDY_SPEED_PASSIVE',
      value: 5.0,
      description: '+5% Tốc độ học chứng chỉ nghề',
      passiveEnergyPerMin: 0,
      passiveHappinessPerMin: 0.15,
    },
    aliases: ['souvenir_wa_needle', 'souvenir_wa_atlas'],
  },

  // 6-15: Ten Bespoke County Atlas & Regional US State Souvenirs
  {
    id: 'souvenir_az_grand_canyon',
    primaryStateCode: 'AZ',
    primaryStateName: 'Arizona',
    coveredStateCodes: ['AZ', 'NM', 'UT'],
    isFlagship: false,
    name: 'Ấn Chương Hẻm Núi Đại Vực Grand Canyon',
    vietnameseName: 'Ấn Chương Hẻm Núi Đại Vực Grand Canyon',
    description: 'Sa bàn vách đá đỏ Grand Canyon phân tầng ôm dòng sông Colorado xanh ngọc và xương rồng Saguaro.',
    pngUrl: './assets/souvenirs/souvenir_az_grand_canyon.png',
    bbox: [44, 35, 425, 443],
    floorAnchor: [0.5, 0.86],
    logicalCanvasSize: [28, 28],
    recommendedDisplaySize: [64, 64],
    buff: {
      type: 'WORK_PAY_PASSIVE',
      value: 10.0,
      description: '+10% Thu nhập Ca làm việc Công nghệ Bán dẫn & Khai khoáng Đồng (+0.2% Vui/p)',
      passiveEnergyPerMin: 0.08,
      passiveHappinessPerMin: 0.2,
    },
    aliases: ['souvenir_az_atlas', 'souvenir_nm_atlas', 'souvenir_ut_atlas', 'souvenir_desert_canyon'],
  },
  {
    id: 'souvenir_or_crater_lake',
    primaryStateCode: 'OR',
    primaryStateName: 'Oregon',
    coveredStateCodes: ['OR'],
    isFlagship: false,
    name: 'Ấn Chương Hồ Miệng Núi Lửa Crater Lake',
    vietnameseName: 'Ấn Chương Hồ Miệng Núi Lửa Crater Lake',
    description: 'Sa bàn hồ miệng núi lửa Crater Lake xanh lam sâu thẳm ôm đảo Wizard Island giữa rừng linh sam Oregon.',
    pngUrl: './assets/souvenirs/souvenir_or_crater_lake.png',
    bbox: [35, 46, 443, 421],
    floorAnchor: [0.5, 0.86],
    logicalCanvasSize: [28, 26],
    recommendedDisplaySize: [64, 62],
    buff: {
      type: 'STUDY_SPEED_PASSIVE',
      value: 10.0,
      description: '+10% Thu nhập Ca làm việc Quang khắc Bán dẫn & Lâm nghiệp (+0.2% Vui/p)',
      passiveEnergyPerMin: 0.08,
      passiveHappinessPerMin: 0.2,
    },
    aliases: ['souvenir_or_atlas'],
  },
  {
    id: 'souvenir_id_star_garnet',
    primaryStateCode: 'ID',
    primaryStateName: 'Idaho',
    coveredStateCodes: ['ID'],
    isFlagship: false,
    name: 'Ấn Chương Ngọc Hồng Lựu Sao Idaho (Star Garnet)',
    vietnameseName: 'Ấn Chương Ngọc Hồng Lựu Sao Idaho',
    description: 'Cụm tinh thể ngọc hồng lựu ánh sao 6 cánh quý hiếm tựa lưng vào đỉnh núi tuyết Sawtooth.',
    pngUrl: './assets/souvenirs/souvenir_id_star_garnet.png',
    bbox: [39, 35, 435, 443],
    floorAnchor: [0.5, 0.86],
    logicalCanvasSize: [28, 28],
    recommendedDisplaySize: [64, 64],
    buff: {
      type: 'ENERGY_PASSIVE',
      value: 10.0,
      description: '+10% Thu nhập Ca làm việc Năng lượng Hạt nhân & Nông sản (+0.15% NL/p)',
      passiveEnergyPerMin: 0.15,
      passiveHappinessPerMin: 0.12,
    },
    aliases: ['souvenir_id_atlas'],
  },
  {
    id: 'souvenir_nv_hoover_dam',
    primaryStateCode: 'NV',
    primaryStateName: 'Nevada',
    coveredStateCodes: ['NV'],
    isFlagship: false,
    name: 'Ấn Chương Bạc Comstock & Đập Hoover',
    vietnameseName: 'Ấn Chương Bạc Comstock & Đập Hoover',
    description: 'Sa bàn đập thủy điện vòng cung Hoover Dam kiến trúc Art Deco giữ làn nước ngọc bích hồ Mead.',
    pngUrl: './assets/souvenirs/souvenir_nv_hoover_dam.png',
    bbox: [43, 35, 427, 443],
    floorAnchor: [0.5, 0.86],
    logicalCanvasSize: [28, 28],
    recommendedDisplaySize: [64, 64],
    buff: {
      type: 'ENERGY_PASSIVE',
      value: 10.0,
      description: '+10% Thu nhập Ca làm việc Địa nhiệt, Lithium & Giải trí (+0.15% NL/p)',
      passiveEnergyPerMin: 0.15,
      passiveHappinessPerMin: 0.12,
    },
    aliases: ['souvenir_nv_atlas'],
  },
  {
    id: 'souvenir_co_rocky_peaks',
    primaryStateCode: 'CO',
    primaryStateName: 'Colorado',
    coveredStateCodes: ['CO', 'MT', 'WY', 'MN', 'WI', 'MI', 'VT', 'NH', 'ME'],
    isFlagship: false,
    name: 'Sa Bàn Đỉnh Tuyết Rocky & Cabin Gỗ Thông',
    vietnameseName: 'Sa Bàn Đỉnh Tuyết Rocky & Cabin Gỗ Thông',
    description: 'Sa bàn song đỉnh núi tuyết Maroon Bells, căn nhà gỗ sáng đèn ấm áp bên rừng bạch dương mùa thu.',
    pngUrl: './assets/souvenirs/souvenir_co_rocky_peaks.png',
    bbox: [43, 35, 427, 443],
    floorAnchor: [0.5, 0.86],
    logicalCanvasSize: [28, 28],
    recommendedDisplaySize: [64, 64],
    buff: {
      type: 'ENERGY_PASSIVE',
      value: 2.5,
      description: '+0.15% Năng lượng & +0.15% Tinh thần thụ động mỗi phút từ không khí núi cao',
      passiveEnergyPerMin: 0.15,
      passiveHappinessPerMin: 0.15,
    },
    aliases: ['souvenir_co_atlas', 'souvenir_snow_peaks'],
  },
  {
    id: 'souvenir_mo_gateway_arch',
    primaryStateCode: 'MO',
    primaryStateName: 'Missouri',
    coveredStateCodes: ['MO', 'KS', 'IL', 'IN', 'IA', 'NE', 'ND', 'SD', 'OK', 'OH', 'AR'],
    isFlagship: false,
    name: 'Sa Bàn Vòm Gateway Arch & Hướng Dương Thảo Nguyên',
    vietnameseName: 'Vòm Gateway Arch & Hướng Dương Thảo Nguyên',
    description: 'Sa bàn vòm thép bạc Gateway Arch bên dòng sông Mississippi, tàu hơi nước cổ điển và hoa hướng dương vàng.',
    pngUrl: './assets/souvenirs/souvenir_mo_gateway_arch.png',
    bbox: [44, 35, 425, 443],
    floorAnchor: [0.5, 0.86],
    logicalCanvasSize: [28, 28],
    recommendedDisplaySize: [64, 64],
    buff: {
      type: 'WORK_PAY_PASSIVE',
      value: 2.5,
      description: '+0.2% Tinh thần thụ động mỗi phút & Cảm hứng giao thương Trung tâm Thảo nguyên',
      passiveEnergyPerMin: 0.08,
      passiveHappinessPerMin: 0.2,
    },
    aliases: ['souvenir_mo_atlas', 'souvenir_ks_atlas', 'souvenir_plains_arch'],
  },
  {
    id: 'souvenir_hi_aloha_volcano',
    primaryStateCode: 'HI',
    primaryStateName: 'Hawaii',
    coveredStateCodes: ['HI'],
    isFlagship: false,
    name: 'Sa Bàn Đảo Núi Lửa Aloha & Hoa Dâm Bụt',
    vietnameseName: 'Đảo Núi Lửa Aloha & Hoa Dâm Bụt',
    description: 'Sa bàn đỉnh núi lửa xanh ngọc lục bảo, sóng biển Waikiki tung bọt trắng và hoa dâm bụt nhiệt đới.',
    pngUrl: './assets/souvenirs/souvenir_hi_aloha_volcano.png',
    bbox: [35, 50, 443, 413],
    floorAnchor: [0.5, 0.86],
    logicalCanvasSize: [28, 26],
    recommendedDisplaySize: [64, 60],
    buff: {
      type: 'HAPPINESS_PASSIVE',
      value: 3.0,
      description: '+0.25% Tinh thần nghỉ dưỡng nhiệt đới Thái Bình Dương mỗi phút',
      passiveEnergyPerMin: 0.05,
      passiveHappinessPerMin: 0.25,
    },
    aliases: ['souvenir_hi_atlas'],
  },
  {
    id: 'souvenir_ak_aurora_totem',
    primaryStateCode: 'AK',
    primaryStateName: 'Alaska',
    coveredStateCodes: ['AK'],
    isFlagship: false,
    name: 'Sa Bàn Đỉnh Tuyết Denali & Dải Cực Quang',
    vietnameseName: 'Đỉnh Tuyết Denali & Dải Cực Quang',
    description: 'Sa bàn đỉnh Denali phủ tuyết trắng, cabin gỗ ấm và dải Cực quang xanh ngọc - tím uốn lượn kỳ ảo.',
    pngUrl: './assets/souvenirs/souvenir_ak_aurora_totem.png',
    bbox: [44, 35, 425, 443],
    floorAnchor: [0.5, 0.86],
    logicalCanvasSize: [28, 28],
    recommendedDisplaySize: [64, 64],
    buff: {
      type: 'ENERGY_PASSIVE',
      value: 3.0,
      description: '+0.15% Năng lượng & +0.15% Tinh thần thụ động mỗi phút dưới ánh Cực quang',
      passiveEnergyPerMin: 0.15,
      passiveHappinessPerMin: 0.15,
    },
    aliases: ['souvenir_ak_atlas'],
  },
  {
    id: 'souvenir_dc_capitol_dome',
    primaryStateCode: 'DC',
    primaryStateName: 'District of Columbia',
    coveredStateCodes: ['DC', 'MD', 'VA', 'PA', 'DE', 'NJ', 'MA', 'CT', 'RI'],
    isFlagship: false,
    name: 'Sa Bàn Mái Vòm Capitol & Hoa Anh Đào',
    vietnameseName: 'Mái Vòm Điện Capitol & Hoa Anh Đào',
    description: 'Sa bàn mái vòm tân cổ điển Điện Capitol soi bóng bên hồ nước phẳng lặng và hàng anh đào nở hồng.',
    pngUrl: './assets/souvenirs/souvenir_dc_capitol_dome.png',
    bbox: [43, 35, 427, 443],
    floorAnchor: [0.5, 0.86],
    logicalCanvasSize: [28, 28],
    recommendedDisplaySize: [64, 64],
    buff: {
      type: 'STUDY_SPEED_PASSIVE',
      value: 4.0,
      description: '+0.2% Tinh thần học thuật & Nghiên cứu di sản Bờ Đông mỗi phút',
      passiveEnergyPerMin: 0.08,
      passiveHappinessPerMin: 0.2,
    },
    aliases: ['souvenir_dc_atlas', 'souvenir_civic_capitol'],
  },
  {
    id: 'souvenir_forest_smoky_mill',
    primaryStateCode: 'TN',
    primaryStateName: 'Appalachia & Blue Ridge',
    coveredStateCodes: ['TN', 'NC', 'KY', 'WV', 'GA', 'SC', 'AL', 'MS', 'LA'],
    isFlagship: false,
    name: 'Sa Bàn Cối Xay Nước Rừng Thông & Lá Phong',
    vietnameseName: 'Cối Xay Nước Rừng Thông & Lá Phong',
    description: 'Sa bàn nhà gỗ cối xay nước bên dòng suối đá rêu phong, rừng thông xanh và tán lá phong mùa thu.',
    pngUrl: './assets/souvenirs/souvenir_forest_smoky_mill.png',
    bbox: [41, 35, 431, 443],
    floorAnchor: [0.5, 0.86],
    logicalCanvasSize: [28, 28],
    recommendedDisplaySize: [64, 64],
    buff: {
      type: 'HAPPINESS_PASSIVE',
      value: 2.5,
      description: '+0.22% Tinh thần thư thái giữa tiếng suối rừng nguyên sinh mỗi phút',
      passiveEnergyPerMin: 0.06,
      passiveHappinessPerMin: 0.22,
    },
    aliases: ['souvenir_forest_mill', 'souvenir_tn_atlas', 'souvenir_nc_atlas'],
  },
] as const;

const SOUVENIR_BY_ID: Record<string, StateSouvenirEntry> = {};
const SOUVENIR_BY_ALIAS: Record<string, StateSouvenirEntry> = {};
const SOUVENIR_BY_STATE_CODE: Record<string, StateSouvenirEntry> = {};

for (const entry of STATE_SOUVENIR_ENTRIES) {
  SOUVENIR_BY_ID[entry.id] = entry;
  SOUVENIR_BY_ALIAS[entry.id.toLowerCase()] = entry;
  for (const alias of entry.aliases) {
    SOUVENIR_BY_ALIAS[alias.toLowerCase()] = entry;
  }
  for (const code of entry.coveredStateCodes) {
    SOUVENIR_BY_STATE_CODE[code.toUpperCase()] = entry;
    SOUVENIR_BY_ALIAS[`souvenir_${code.toLowerCase()}_atlas`] = entry;
  }
}

const CANONICAL_STATE_NAMES_TO_CODE: Record<string, string> = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  cali: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  'district of columbia': 'DC',
  'washington dc': 'DC',
  'washington d c': 'DC',
  'd c': 'DC',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  'hawai i': 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'washington state': 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
};

const STATE_NAME_TO_CODE: Record<string, string> = {};
for (const [name, code] of Object.entries(CANONICAL_STATE_NAMES_TO_CODE)) {
  STATE_NAME_TO_CODE[name] = code;
  STATE_NAME_TO_CODE[name.replace(/\s+/g, '')] = code;
}

const FIPS_PREFIX_TO_STATE_CODE: Record<string, string> = {
  '01': 'AL',
  '02': 'AK',
  '04': 'AZ',
  '05': 'AR',
  '06': 'CA',
  '08': 'CO',
  '09': 'CT',
  '10': 'DE',
  '11': 'DC',
  '12': 'FL',
  '13': 'GA',
  '15': 'HI',
  '16': 'ID',
  '17': 'IL',
  '18': 'IN',
  '19': 'IA',
  '20': 'KS',
  '21': 'KY',
  '22': 'LA',
  '23': 'ME',
  '24': 'MD',
  '25': 'MA',
  '26': 'MI',
  '27': 'MN',
  '28': 'MS',
  '29': 'MO',
  '30': 'MT',
  '31': 'NE',
  '32': 'NV',
  '33': 'NH',
  '34': 'NJ',
  '35': 'NM',
  '36': 'NY',
  '37': 'NC',
  '38': 'ND',
  '39': 'OH',
  '40': 'OK',
  '41': 'OR',
  '42': 'PA',
  '44': 'RI',
  '45': 'SC',
  '46': 'SD',
  '47': 'TN',
  '48': 'TX',
  '49': 'UT',
  '50': 'VT',
  '51': 'VA',
  '53': 'WA',
  '54': 'WV',
  '55': 'WI',
  '56': 'WY',
};

const FLAGSHIP_STATE_CODES = new Set<string>(['NY', 'CA', 'TX', 'FL', 'WA']);

/**
 * Normalizes a 2-letter code or full state slug ('new_york', 'New-Mexico', 'California', 'az')
 * into an uppercase 2-letter US state code.
 */
export function normalizeUsStateCodeInput(input: string): string {
  if (!input || typeof input !== 'string') return '';
  const trimmed = input.trim();
  if (!trimmed) return '';
  const normalizedWords = trimmed
    .toLowerCase()
    .replace(/[._'’-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (STATE_NAME_TO_CODE[normalizedWords]) {
    return STATE_NAME_TO_CODE[normalizedWords];
  }
  const compact = normalizedWords.replace(/\s+/g, '');
  if (STATE_NAME_TO_CODE[compact]) {
    return STATE_NAME_TO_CODE[compact];
  }
  return trimmed.toUpperCase();
}

/**
 * Resolves any souvenir ID, legacy alias ('souvenir_ny_statue', 'souvenir_ca_bridge', 'souvenir_az_atlas', 'souvenir_pa_atlas'),
 * or 2-letter state code ('NY', 'AZ', 'HI') to its StateSouvenirEntry.
 */
export function getSouvenirEntry(souvenirIdOrStateCode: string): StateSouvenirEntry | null {
  if (!souvenirIdOrStateCode || typeof souvenirIdOrStateCode !== 'string') return null;
  const trimmed = souvenirIdOrStateCode.trim();
  if (!trimmed) return null;

  const lower = trimmed.toLowerCase();
  if (SOUVENIR_BY_ALIAS[lower]) {
    return SOUVENIR_BY_ALIAS[lower];
  }

  const stateCode = normalizeUsStateCodeInput(trimmed);
  if (SOUVENIR_BY_STATE_CODE[stateCode]) {
    return SOUVENIR_BY_STATE_CODE[stateCode];
  }

  return null;
}

/**
 * Normalizes a souvenir ID or alias to its canonical asset key (e.g. 'souvenir_ny_statue' -> 'souvenir_ny_statue_liberty').
 */
export function normalizeSouvenirId(souvenirIdOrAlias: string): string {
  if (!souvenirIdOrAlias || typeof souvenirIdOrAlias !== 'string') return '';
  const entry = getSouvenirEntry(souvenirIdOrAlias);
  return entry ? entry.id : souvenirIdOrAlias.trim();
}

/**
 * Resolves the authentic 2.5D Gouache StateSouvenirEntry for any of the 50 US states + DC.
 */
export function getSouvenirForStateCode(stateCodeOrSlug: string): StateSouvenirEntry {
  const code = normalizeUsStateCodeInput(stateCodeOrSlug);
  return SOUVENIR_BY_STATE_CODE[code] || SOUVENIR_BY_ID.souvenir_ny_statue_liberty;
}

/**
 * Returns all 15 bespoke Gouache State & Regional Souvenirs (5 Flagship + 10 Atlas/Regional).
 */
export function getAllSouvenirEntries(): readonly StateSouvenirEntry[] {
  return STATE_SOUVENIR_ENTRIES;
}

/**
 * Checks whether a souvenir (or any of its legacy/regional `souvenir_<code>_atlas` aliases)
 * is unlocked in `inventory.unlockedSouvenirs`.
 */
export function isSouvenirUnlocked(
  souvenirIdOrStateCode: string,
  unlockedSouvenirs: readonly string[] | undefined | null
): boolean {
  if (!souvenirIdOrStateCode || !Array.isArray(unlockedSouvenirs) || unlockedSouvenirs.length === 0) {
    return false;
  }
  const entry = getSouvenirEntry(souvenirIdOrStateCode);
  if (!entry) {
    return unlockedSouvenirs.includes(souvenirIdOrStateCode);
  }
  for (const item of unlockedSouvenirs) {
    if (typeof item !== 'string') continue;
    const cleaned = item.trim().toLowerCase();
    if (!cleaned) continue;
    const resolved = SOUVENIR_BY_ALIAS[cleaned];
    if (resolved && resolved.id === entry.id) {
      return true;
    }
  }
  return false;
}

/**
 * Retroactively backfills canonical regional Gouache souvenirs into `state.inventory.unlockedSouvenirs`
 * for any non-flagship US states or counties already visited in `visitedAtlasStates`, `visitedCounties`,
 * or `visitedCaCounties` (preserving strict flight/check-in unlock rules for the 5 flagship states).
 */
export function backfillNonFlagshipSouvenirsFromVisits(state: {
  inventory?: { unlockedSouvenirs?: string[] };
  visitedAtlasStates?: Record<string, { code?: string; visitCount?: number }>;
  visitedCounties?: Record<string, { fips?: string; stateCode?: string; visitCount?: number }>;
  visitedCaCounties?: Record<string, { fips?: string; stateCode?: string; visitCount?: number }>;
} | null | undefined): string[] {
  if (!state || typeof state !== 'object' || !state.inventory || !Array.isArray(state.inventory.unlockedSouvenirs)) {
    return [];
  }

  const unlocked = state.inventory.unlockedSouvenirs;
  const newlyAdded: string[] = [];

  const maybeUnlockForStateCode = (rawCode: string | undefined): void => {
    if (!rawCode) return;
    const code = normalizeUsStateCodeInput(rawCode);
    if (!code || FLAGSHIP_STATE_CODES.has(code)) return;
    const entry = SOUVENIR_BY_STATE_CODE[code];
    if (!entry) return;
    if (!isSouvenirUnlocked(entry.id, unlocked)) {
      unlocked.push(entry.id);
      newlyAdded.push(entry.id);
    }
  };

  if (state.visitedAtlasStates && typeof state.visitedAtlasStates === 'object') {
    for (const [key, rec] of Object.entries(state.visitedAtlasStates)) {
      if (!rec || typeof rec !== 'object') continue;
      if (typeof rec.visitCount === 'number' && rec.visitCount <= 0) continue;
      maybeUnlockForStateCode(rec.code || key);
    }
  }

  const scanCountyMap = (
    countyMap: Record<string, { fips?: string; stateCode?: string; visitCount?: number }> | undefined
  ): void => {
    if (!countyMap || typeof countyMap !== 'object') return;
    for (const [key, rec] of Object.entries(countyMap)) {
      if (!rec || typeof rec !== 'object') continue;
      if (typeof rec.visitCount === 'number' && rec.visitCount <= 0) continue;
      if (rec.stateCode) {
        maybeUnlockForStateCode(rec.stateCode);
        continue;
      }
      const rawFips = String(rec.fips || key).trim();
      if (/^\d{4,5}$/.test(rawFips)) {
        const padded = rawFips.padStart(5, '0');
        maybeUnlockForStateCode(FIPS_PREFIX_TO_STATE_CODE[padded.slice(0, 2)]);
      }
    }
  };

  scanCountyMap(state.visitedCounties);
  scanCountyMap(state.visitedCaCounties);

  return newlyAdded;
}

/**
 * Checks whether a souvenir (or any of its legacy aliases) is currently placed in `placedFurniture`.
 */
export function isSouvenirPlaced(
  souvenirIdOrStateCode: string,
  placedFurniture:
    | ReadonlyArray<{ typeId: string; customData?: { souvenirId?: string } }>
    | undefined
    | null
): boolean {
  if (!souvenirIdOrStateCode || !Array.isArray(placedFurniture) || placedFurniture.length === 0) {
    return false;
  }
  const targetCanonical = normalizeSouvenirId(souvenirIdOrStateCode);
  return placedFurniture.some((item) => {
    if (item.typeId !== 'souvenir' || !item.customData?.souvenirId) return false;
    return normalizeSouvenirId(item.customData.souvenirId) === targetCanonical;
  });
}
